// productReplaceSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchReplaceVoucherNumber,
  createProductReplace,
  fetchSalesVouchers,
  fetchSalesVoucherDetails,
  fetchFilteredVouchers,
  fetchFilteredItems,
  fetchGivenItemsList,
  fetchCustomersList,
  fetchItemBySearch,
  fetchTaxAccs,
  fetchReplaceRecordWithDetail,
} from "./productReplaceThunk";

export const emptyGivenRow = (i = 1) => ({
  key: `g-${i}`,
  product: "",
  item_id: null,
  quantity: 0,
  rate: 0,
  tax_per: 0,
  batch_id: null,
  fullItem: null,
});
export const emptyReturnRow = (i = 1) => ({
  key: `r-${i}`,
  product: "",
  item_id: null,
  quantity: 0,
  rate: 0,
  tax_per: 0,
  fullItem: null,
});

const initialState = {
  customersList: [],
  salesVouchersList: [],
  selectedVoucherDetails: null,
  customerInfo: {},
  itemSearchList: [],
  givenItemsList: [emptyGivenRow()],
  availableStock: [],
  returnItemsList: [],
  footerValues: {
    payServiceCart: [],
    paidAmount: 0,
    serviceCharge: 0,
    description: "",
  },
  selectedTaxAcc: null,
  voucherNumber: "",
  replaceSuccess: null,
  loading: false,
};

const productReplaceSlice = createSlice({
  name: "productReplace",
  initialState,
  reducers: {
    setResetProductReplace(state) {
      state.replaceSuccess = null;
      state.customerInfo = {};
      state.itemSearchList = [];
      state.availableStock = [];
      state.returnItemsList = [];
      state.givenItemsList = [emptyGivenRow()];
      state.footerValues = {};
      state.selectedTaxAcc = null;
      state.salesVouchersList = [];
      state.selectedVoucherDetails = null;
    },
    setSelectedCustomer(state, action) {
      state.customerInfo = action.payload;
    },
    updateCustomerField(state, action) {
      const { field, value } = action.payload;
      state.customerInfo[field] = value;
    },
    setFormField(state, action) {
      const { field, value } = action.payload;
      state[field] = value;
    },
    setEditRecord: (state, action) => {
      state.isEdit = action.payload.isEdit;
      state.selectedRecord = action.payload.selectedRecord;
    },
    clearEditRecord: (state) => {
      state.isEdit = false;
      state.selectedRecord = null;
    },

    // table helpers
    addGivenRow(state) {
      state.givenItemsList.push(emptyGivenRow(state.givenItemsList.length + 1));
    },
    deleteGivenRow(state, action) {
      state.givenItemsList = state.givenItemsList.filter(
        (r) => r.key !== action.payload
      );
      if (!state.givenItemsList.length)
        state.givenItemsList = [emptyGivenRow()];
    },
    addReturnRow(state) {
      state.returnItemsList.push(
        emptyReturnRow(state.returnItemsList.length + 1)
      );
    },
    deleteReturnRow(state, action) {
      state.returnItemsList = state.returnItemsList.filter(
        (r) => r.key !== action.payload
      );
    },

    setGivenRowItem(state, action) {
      const { rowKey, itemData } = action.payload;
      state.givenItemsList = state.givenItemsList.map((row) =>
        row.key === rowKey
          ? {
              ...row,
              ...itemData, // merge all fields from API
              product: `${itemData.item_name} - ${itemData.item_code || ""}`,
              item_id: itemData.item_id,
              rate: itemData.item_rate ?? itemData.sale_rate ?? 0,
              quantity: row.quantity || 1,
              tax_per: itemData.tax_per ?? 0,
              fullItem: itemData,
            }
          : row
      );
    },

    calculateFooterTotals: (state) => {
      let givenTotalVat = 0;
      const givenTotal = state.givenItemsList.reduce((sum, item) => {
        const sub = Number(item.quantity || 0) * Number(item.rate || 0);
        const vat = sub * (Number(item.tax_per || 0) / 100);
        givenTotalVat += vat;
        return sum + sub + vat;
      }, 0);

      const returnTotal = state.returnItemsList.reduce((sum, item) => {
        const sub = Number(item.quantity || 0) * Number(item.rate || 0);
        const vat = sub * (Number(item.tax_per || 0) / 100);
        // return sum + sub + vat;
        return sum + sub ;
      }, 0);

      const subtotal = givenTotal - returnTotal;

      // ✅ ADDITIONAL EXPENSES INCLUDED HERE
      const additionalExpenses = (
        state.footerValues.payServiceCart || []
      ).reduce((sum, row) => sum + Number(row.tran_amount || 0), 0);

      // const sumExcludingCheque = _.sumBy(
      //   state.footerValues.payCart.filter(
      //     (pay) => pay.acc_type_id !== "cheque"
      //   ),
      //   (pay) => Number(pay.tran_amount)
      // );

      const sumExcludingCheque = (state.footerValues.payCart || [])
        .filter((row) => row.acc_type_id !== "cheque")
        .reduce((sum, row) => sum + Number(row.tran_amount || 0), 0);

      const serviceCharge = Number(state.footerValues.serviceCharge || 0);

      const grandTotal = subtotal + serviceCharge + additionalExpenses;

      const isNegative = grandTotal < 0;
   

      const paidAmount = isNegative
            ? -Math.abs(sumExcludingCheque).toFixed(2)
            : parseFloat(sumExcludingCheque?.toFixed(2));

      const dueAmount = grandTotal - paidAmount;

      state.footerValues = {
        ...state.footerValues,
        givenTotal: givenTotal.toFixed(2),
        returnTotal: returnTotal.toFixed(2),
        subtotal: subtotal.toFixed(2),
        additionalExpenses: additionalExpenses.toFixed(2), // optional display
        grandTotal: grandTotal.toFixed(2),
        dueAmount: dueAmount.toFixed(2),
        paidAmount: paidAmount.toFixed(2)
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReplaceVoucherNumber.fulfilled, (state, action) => {
        state.voucherNumber = action.payload;
      })
      .addCase(fetchFilteredVouchers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFilteredVouchers.fulfilled, (state, action) => {
        state.loading = false;
        state.salesVouchersList = action.payload;
      })
      .addCase(fetchFilteredVouchers.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createProductReplace.pending, (state) => {
        state.loading = true;
      })
      .addCase(createProductReplace.fulfilled, (state, action) => {
        state.loading = false;
        state.replaceSuccess = action.payload;
      })
      .addCase(createProductReplace.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchSalesVouchers.fulfilled, (state, action) => {
        state.salesVouchersList = action.payload;
      })
      .addCase(fetchCustomersList.fulfilled, (state, action) => {
        state.customersList = action.payload;
      })
      .addCase(fetchSalesVoucherDetails.fulfilled, (state, action) => {
        state.selectedVoucherDetails = {
          ...(state.selectedVoucherDetails || {}),
          ...action.payload, // adds salesDetails + returnedItems
        };
      })
      .addCase(fetchReplaceRecordWithDetail.fulfilled, (state, action) => {
        state.customerInfo = action.payload.customerInfo;
        state.voucherNumber = action.payload.voucherNumber;
        state.givenItemsList = action.payload.givenItemsList;
        state.returnItemsList = action.payload.returnItemsList;
        state.footerValues = {
          ...state.footerValues,
          ...action.payload.footerValues,
        };
        state.selectedVoucherDetails = action.payload.selectedVoucherDetails;
      })

      // When an invoice is chosen, hydrate customer + RETURN items
      .addCase(fetchFilteredItems.fulfilled, (state, action) => {
        const { customerInfo, items } = action.payload;
        state.customerInfo = customerInfo;

        // map to our table row shape
        state.returnItemsList = items.map((it, idx) => ({
          key: `r-${idx + 1}`,
          item_id: it.item_id,
          product: it.item_name,
          quantity: Number(it.return_qty || 0),
          rate: Number(it.return_rate ?? it.item_return_unit_price ?? 0),
          tax_per: Number(it.item_tax_per || 0), // for reference
          available_qty: it.available_qty ?? it.sold_qty ?? 0,
        }));
      })

      .addCase(fetchItemBySearch.fulfilled, (state, action) => {
        state.itemSearchList = action.payload.map((item) => {
          return {
            ...item,
            item_id: item.item_id,
            item_rate: item.item_rate ?? item.sale_rate ?? 0,
            unit_price: item.item_rate ?? item.sale_rate ?? 0,
            display_text: item.display_text,
            tax_per: item.tax_per ?? 0,
          };
        });
      })
      .addCase(fetchGivenItemsList.fulfilled, (state, action) => {
        state.availableStock = action.payload;
      })
      .addCase(fetchTaxAccs.fulfilled, (state, action) => {
        state.selectedTaxAcc = action.payload;
      });
  },
});

export const {
  setResetProductReplace,
  setSelectedCustomer,
  updateCustomerField,
  setFormField,
  addGivenRow,
  deleteGivenRow,
  addReturnRow,
  deleteReturnRow,
  calculateFooterTotals,
} = productReplaceSlice.actions;

export default productReplaceSlice.reducer;
